import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { appelApi, installeEnTetesAuth } from '../api/client';
import type { Moi } from '../api/types';

/**
 * Session — §5.2 de la spec.
 *
 * Authorization code + PKCE contre Entra ID, mono-tenant : c'est MSAL qui le
 * fait, sans session maison ni mot de passe stocké. Le mode `dev` existe pour
 * travailler sans app registration : l'identité tient alors dans un en-tête, et
 * l'API le refuse en production.
 *
 * La configuration (mode, tenant, client id, portée) est lue à l'exécution sur
 * `GET /api/configuration`, plus jamais figée dans le bundle : les `VITE_ENTRA_*`
 * du build ont produit trois fois le même AADSTS900144 — un système de CI ou un
 * poste oubliait de les passer, et MSAL partait avec un client id vide. L'API,
 * elle, refuse de démarrer sans ces valeurs en mode entra : une seule source,
 * une seule image valable partout.
 */

type ConfigurationAuth = {
  authMode: 'entra' | 'dev';
  entra: { tenantId: string; clientId: string; scope: string } | null;
};

let configuration: ConfigurationAuth | undefined;
let configurationEnCours: Promise<ConfigurationAuth> | undefined;

async function chargeConfiguration(): Promise<ConfigurationAuth> {
  if (configuration) return configuration;
  // Une seule requête même si plusieurs appelants arrivent en même temps.
  configurationEnCours ??= appelApi<ConfigurationAuth>('/api/configuration').then((c) => {
    configuration = c;
    return c;
  });
  return configurationEnCours;
}

const CLE_DEV = 'skills-elyadata.dev-user';

/** Comptes proposés par le sélecteur du mode dev (ceux du jeu de démonstration). */
/**
 * Le rôle affiché ici est indicatif : le rôle réel vient de la table
 * `personne` (ou de `ADMIN_EMAILS`), pas de cette liste. `amine.belmabrouk` et
 * `salim.jouili` sont admins par `ADMIN_EMAILS`, quel que soit ce que le seed
 * a peuplé pour eux — les trois autres sont des personnes réelles du jeu de
 * démonstration (§ `prisma/seed.ts`, export de juin 2024), avec leur grade et
 * leurs appartenances déjà en place.
 */
export const IDENTITES_DEV = [
  { email: 'amine.belmabrouk@elyadata.com', role: 'admin' },
  { email: 'salim.jouili@elyadata.com', role: 'admin' },
  { email: 'achref.khemiri@elyadata.com', role: 'encadrement' },
  { email: 'nesrine.kanzari@elyadata.com', role: 'encadrement' },
  { email: 'chaima.yaakoubi@elyadata.com', role: 'collaborateur' },
  { email: 'amira.karoui@elyadata.com', role: 'collaborateur' },
];

let msal: PublicClientApplication | undefined;
let msalPrete: Promise<void> | undefined;

function clientMsal(entra: { tenantId: string; clientId: string }): PublicClientApplication {
  if (!msal) {
    msal = new PublicClientApplication({
      auth: {
        clientId: entra.clientId,
        authority: `https://login.microsoftonline.com/${entra.tenantId}`,
        redirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
    });
    msalPrete = msal.initialize();
  }
  return msal;
}

/**
 * Quel jeton envoyer à l'API — deux configurations possibles, toutes deux
 * acceptées côté serveur (l'audience valide est `<clientId>` ou
 * `api://<clientId>`) :
 *
 * 1. **Sans rien exposer côté Azure** (défaut, `scope` vide). Le SPA demande
 *    `openid profile email` et envoie le jeton d'identité, dont l'audience est
 *    le client id. C'est ce qui marche avec une app registration « single-page
 *    application » nue, donc ce qui permet de brancher le SSO le jour même.
 * 2. **Avec « Expose an API »**. Renseigner `ENTRA_SCOPE_SPA` côté API, par
 *    exemple `api://<clientId>/access_as_user` : le SPA demande alors un jeton
 *    d'accès, ce qui est la forme propre — un jeton d'identité n'est pas fait
 *    pour autoriser un appel d'API.
 */
function porteeDe(entra: { scope: string }): string[] {
  return entra.scope.trim() ? [entra.scope.trim()] : ['openid', 'profile', 'email'];
}

async function jetonEntra(
  compte: AccountInfo,
  entra: { tenantId: string; clientId: string; scope: string },
): Promise<string> {
  const client = clientMsal(entra);
  await msalPrete;

  const scopes = porteeDe(entra);
  const prendre = (resultat: { accessToken: string; idToken: string }): string =>
    entra.scope.trim() ? resultat.accessToken : resultat.idToken;

  try {
    return prendre(await client.acquireTokenSilent({ account: compte, scopes }));
  } catch (cause) {
    if (cause instanceof InteractionRequiredAuthError) {
      return prendre(await client.acquireTokenPopup({ account: compte, scopes }));
    }
    throw cause;
  }
}

export type Session = {
  mode: 'entra' | 'dev';
  moi: Moi | undefined;
  enCours: boolean;
  erreur: string | undefined;
  identiteDev: string | null;
  connecte: (email?: string) => Promise<void>;
  deconnecte: () => Promise<void>;
  rafraichit: () => Promise<void>;
  estAuMoins: (role: 'collaborateur' | 'encadrement' | 'admin') => boolean;
};

const Contexte = createContext<Session | undefined>(undefined);

const RANGS = { collaborateur: 0, encadrement: 1, admin: 2 } as const;

export function FournisseurSession({ children }: { children: ReactNode }): JSX.Element {
  const [moi, setMoi] = useState<Moi | undefined>(undefined);
  const [enCours, setEnCours] = useState(true);
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  // Le mode réel arrive avec la configuration ; `entra` en attendant — l'écran
  // est de toute façon en chargement tant qu'elle n'est pas là.
  const [mode, setMode] = useState<'entra' | 'dev'>('entra');
  const [identiteDev, setIdentiteDev] = useState<string | null>(
    () => window.localStorage.getItem(CLE_DEV),
  );

  // Les en-têtes d'authentification sont posés une fois pour tout le client.
  // Le fournisseur lit la configuration module (posée par `chargeConfiguration`)
  // et non l'état React : il est appelé hors du rendu, par `appelApi`. Tant
  // qu'elle n'est pas là, pas d'en-tête — la seule requête d'avant est
  // `/api/configuration` elle-même, qui est publique.
  useEffect(() => {
    installeEnTetesAuth(async (): Promise<Record<string, string>> => {
      if (!configuration) return {};
      if (configuration.authMode === 'dev') {
        const email = window.localStorage.getItem(CLE_DEV);
        return email ? { 'x-dev-user': email } : {};
      }
      if (!configuration.entra) return {};
      const client = clientMsal(configuration.entra);
      await msalPrete;
      const compte = client.getActiveAccount() ?? client.getAllAccounts()[0];
      if (!compte) return {};
      return { authorization: `Bearer ${await jetonEntra(compte, configuration.entra)}` };
    });
  }, []);

  const charge = useCallback(async () => {
    setEnCours(true);
    setErreur(undefined);
    try {
      setMoi(await appelApi<Moi>('/api/moi'));
    } catch (cause) {
      setMoi(undefined);
      setErreur(cause instanceof Error ? cause.message : 'Sign-in failed');
    } finally {
      setEnCours(false);
    }
  }, []);

  useEffect(() => {
    let annule = false;

    async function demarre(): Promise<void> {
      const config = await chargeConfiguration();
      if (!annule) setMode(config.authMode);

      if (config.authMode === 'entra') {
        if (!config.entra) throw new Error('The API did not provide the Entra configuration');
        const client = clientMsal(config.entra);
        await msalPrete;
        const resultat = await client.handleRedirectPromise();
        if (resultat?.account) client.setActiveAccount(resultat.account);
        const compte = client.getActiveAccount() ?? client.getAllAccounts()[0];
        if (!compte) {
          if (!annule) setEnCours(false);
          return;
        }
        client.setActiveAccount(compte);
      } else if (!window.localStorage.getItem(CLE_DEV)) {
        if (!annule) setEnCours(false);
        return;
      }
      if (!annule) await charge();
    }

    void demarre().catch((cause: unknown) => {
      if (annule) return;
      setErreur(cause instanceof Error ? cause.message : 'Sign-in failed');
      setEnCours(false);
    });

    return () => {
      annule = true;
    };
  }, [charge]);

  const connecte = useCallback(
    async (email?: string) => {
      const config = await chargeConfiguration();

      if (config.authMode === 'dev') {
        const choisi = email ?? IDENTITES_DEV[0]!.email;
        window.localStorage.setItem(CLE_DEV, choisi);
        setIdentiteDev(choisi);
        await charge();
        return;
      }

      /**
       * Redirection plutôt que popup : c'est le flux qui survit aux bloqueurs de
       * fenêtres, et il évite l'avertissement `Cross-Origin-Opener-Policy` que
       * MSAL provoque en surveillant `window.closed` sur la popup. Le retour est
       * repris par `handleRedirectPromise()` au démarrage.
       *
       * Le renouvellement de jeton, lui, reste en popup (`jetonEntra`) : une
       * redirection en pleine saisie ferait perdre la ligne en cours.
       */
      if (!config.entra) throw new Error('The API did not provide the Entra configuration');
      const client = clientMsal(config.entra);
      await msalPrete;
      await client.loginRedirect({ scopes: porteeDe(config.entra), prompt: 'select_account' });
    },
    [charge],
  );

  const deconnecte = useCallback(async () => {
    const config = await chargeConfiguration();
    if (config.authMode === 'dev') {
      window.localStorage.removeItem(CLE_DEV);
      setIdentiteDev(null);
      setMoi(undefined);
      return;
    }
    if (!config.entra) return;
    const client = clientMsal(config.entra);
    await msalPrete;
    await client.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, []);

  const valeur = useMemo<Session>(
    () => ({
      mode,
      moi,
      enCours,
      erreur,
      identiteDev,
      connecte,
      deconnecte,
      rafraichit: charge,
      estAuMoins: (role) => (moi ? RANGS[moi.personne.role] >= RANGS[role] : false),
    }),
    [mode, moi, enCours, erreur, identiteDev, connecte, deconnecte, charge],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): Session {
  const session = useContext(Contexte);
  if (!session) throw new Error('useSession must be used inside FournisseurSession');
  return session;
}
